import { EPaletteModes } from '@/types/theme-packs.types';
import {
  parseColor,
  resolveColor,
  formatTimestamp,
  eventImage,
  getHeaderImage,
  getBtvFallbackForBg,
  description as getDescription,
} from './utils/utils';
import type { TemplateProps } from './index';
import chroma from 'chroma-js';
import ContentEditable from './content-editable';
import { useImageUpload } from './utils/use-image-upload';
import styles from './template-styles.module.css';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';

export const News = ({
  event,
  theme,
  mode,
  calendarLinks,
  customizations,
  href,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
}: TemplateProps) => {
  const resolvedMode = mode ?? event.settings.design.color_theme ?? theme.theme_mode ?? EPaletteModes.Light;
  const baseBg = resolvedMode === EPaletteModes.Dark ? '#000000' : '#FFFFFF';
  const themeColors = theme.colors[resolvedMode] ?? theme.colors;
  const bg =
    customizations?.backgroundColor ||
    chroma
      .mix(
        baseBg,
        customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor, { opacity: 0.5 }),
        0.05,
        'rgb'
      )
      .css();
  const container = resolveColor(themeColors.containerColor, customizations?.containerColor);
  const buttonBg = resolveColor(themeColors.accentColor, customizations?.accentColor);

  const body = getRelativeContrastColor(
    resolveColor(themeColors.bodyTextColor, customizations?.bodyTextColor),
    container
  );
  const buttonText = getRelativeContrastColor(
    resolveColor(themeColors.buttonTextColor ?? themeColors.bodyTextColor, customizations?.buttonTextColor),
    buttonBg
  );
  const [loadingMain, uploadMain] = useImageUpload(setMainImage);
  const [loadingHeaderImg, uploadHeaderImg] = useImageUpload(setHeaderImage);
  const sessionDate = event.sessions[0]?.timestamp ? formatTimestamp(event.sessions[0].timestamp) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || getBtvFallbackForBg(container);
  const bodyImg = customizations?.mainImage || eventImage(event);
  const headingFont = customizations?.headingFont ? `${customizations.headingFont}, sans-serif` : 'Arial, sans-serif';
  const bodyFont = customizations?.bodyFont ? `${customizations.bodyFont}, sans-serif` : 'Courier New, monospace';
  const _salutation = salutation ?? (customizations?.salutation || 'You have been invited to');
  const eventTitle = title ?? (customizations?.title || event.name);
  const eventDescription = description ?? customizations?.description ?? getDescription(event, 'en', 159, 5, 14);
  const _cta = cta ?? (customizations?.cta || 'REGISTER');

  return (
    <table
      data-component="News"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--body': body,
          '--button-bg': buttonBg,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '40px 40px 24px 40px' }}>
            <table
              width={520}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '520px', background: 'var(--container)' }}
            >
              <tbody>
                {/* Header row with decorative line */}
                <tr>
                  <td>
                    <table width="100%">
                      <tbody>
                        <tr>
                          <td
                            style={{
                              fontSize: 0,
                              lineHeight: '0',
                              verticalAlign: 'middle',
                              width: '101px',
                              paddingLeft: '16px',
                            }}
                          >
                            {headerImg && (
                              <div
                                onClick={uploadHeaderImg}
                                data-cont="img"
                                className={loadingHeaderImg ? styles.imgLoading : undefined}
                              >
                                <img src={headerImg} height="12px" style={{ margin: 'auto' }} />
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '16px' }}>
                            <div style={{ borderTop: '1px solid var(--body)', fontSize: 0, lineHeight: '0' }}>
                              &nbsp;
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                {/* Title section */}
                <tr>
                  <td style={{ padding: '8px 24px 0 24px' }}>
                    <ContentEditable
                      value={_salutation}
                      setValue={setSalutation}
                      style={{
                        color: 'var(--body)',
                        fontSize: '16px',
                        fontFamily: 'var(--body-font)',
                        fontWeight: 300,
                        textTransform: 'uppercase',
                        lineHeight: '12.8px',
                        letterSpacing: '0.48px',
                        wordWrap: 'break-word',
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 24px 16px 24px' }}>
                    <ContentEditable
                      value={eventTitle}
                      setValue={setTitle}
                      style={{
                        color: 'var(--body)',
                        fontSize: '64px',
                        fontFamily: 'var(--heading-font)',
                        fontWeight: 400,
                        textTransform: 'uppercase',
                        lineHeight: '64px',
                        wordWrap: 'break-word',
                        maxWidth: '492px',
                      }}
                    />
                  </td>
                </tr>
                {/* Divider */}
                <tr>
                  <td style={{ padding: '0 16px 24px 16px' }}>
                    <div style={{ borderTop: '1px solid var(--body)', fontSize: 0, lineHeight: '0' }}>&nbsp;</div>
                  </td>
                </tr>
                {/* Image + brief info row */}
                <tr>
                  <td style={{ padding: '0 16px' }}>
                    <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td width={297} valign="top" style={{ fontSize: 0, lineHeight: '0' }}>
                            {bodyImg && (
                              <div
                                onClick={uploadMain}
                                data-cont="img"
                                className={loadingMain ? styles.imgLoading : undefined}
                              >
                                <img width={297} src={bodyImg} style={{ display: 'block' }} alt="" />
                              </div>
                            )}
                          </td>
                          {eventDescription && (
                            <td valign="top" style={{ padding: '16px', verticalAlign: 'top' }}>
                              <ContentEditable
                                value={eventDescription}
                                setValue={setDescription}
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '14px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 300,
                                  lineHeight: '16.8px',
                                  wordWrap: 'break-word',
                                  maxWidth: '159px',
                                }}
                              />
                            </td>
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                {/* Divider */}
                <tr>
                  <td style={{ padding: '24px 16px' }}>
                    <div style={{ borderTop: '1px solid var(--body)', fontSize: 0, lineHeight: '0' }}>&nbsp;</div>
                  </td>
                </tr>
                {/* Date + register row */}
                <tr>
                  <td style={{ padding: '0 16px 32px 16px' }}>
                    <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          {sessionDate && (
                            <td valign="middle" style={{ verticalAlign: 'middle' }}>
                              <div
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '20px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 500,
                                  textTransform: 'uppercase',
                                  lineHeight: '20px',
                                  letterSpacing: '0.4px',
                                  wordWrap: 'break-word',
                                  maxWidth: '60%',
                                }}
                              >
                                {sessionDate}
                              </div>
                            </td>
                          )}
                          <td valign="middle" align="right" style={{ verticalAlign: 'middle' }}>
                            <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                <tr>
                                  <td
                                    align="center"
                                    valign="middle"
                                    style={{ background: 'var(--button-bg)', width: '148px', height: '40px' }}
                                  >
                                    <a
                                      href={anchorHref}
                                      onClick={(e) => e.preventDefault()}
                                      style={{
                                        textDecoration: 'none',
                                        padding: '6px 0px 6px 0px',
                                        width: '148px',
                                        height: '100%',
                                      }}
                                    >
                                      <ContentEditable
                                        value={_cta}
                                        setValue={setCta}
                                        style={{
                                          color: 'var(--button-text)',
                                          fontSize: '13px',
                                          fontFamily: 'var(--heading-font)',
                                          fontWeight: 700,
                                          lineHeight: '16px',
                                          wordWrap: 'break-word',
                                        }}
                                      />
                                    </a>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
            <table cellPadding={0} cellSpacing={0} border={0} role="presentation" style={{ marginTop: '40px' }}>
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.iCal}
                      >
                        iCal
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.outlook}
                      >
                        Outlook
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.google}
                      >
                        Google
                      </a>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
